import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { catchRenderException, HpmsFormTechnicalVisitAPI, shouldRenderMobile } from '@philips-emr/hpms-health-insurance-components';
import { ActionAPI, DeckAPI, FetchAPI, FormAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnChange, OnClick, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { TechnicalVisitFormAliases as Aliases, FragmentNames } from '../../codegen';
import { ToastService } from '../../services';
import DownloadService from '../../services/download-service';
import BaseController from '../BaseController';

interface SubObject {
  code: number;
  description: string;
  ieRequired: string;
}

interface Form {
  nrSeqVisit: number;
  nrSeqForm: number;
  nrSeqGroup: number;
  dsGroup: string;
  dsAttachment: string;
  nrSequence: number;
}

interface Status {
  ieAllowStart: string;
  ieAllowFinish: string;
  ieAllowEdit: string;
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_FORM })
export default class TechnicalVisitController extends BaseController {

  @Inject(Aliases.PANEL_GENERAL_FORMS)
  private panelGeneralForms: PanelAPI;

  @Inject(Aliases.PANEL_ADDITIONAL_INFORMATION)
  private panelAdditionalInformation: PanelAPI;

  @Inject(Aliases.DECK_MENU)
  private deck: DeckAPI;

  @Inject(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  private hpmsFormTechnicalVisit: HpmsFormTechnicalVisitAPI;

  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;

  @Inject(Aliases.FORM_SEND_ATTACHMENTS)
  private formSendAttachments: FormAPI;

  @Inject(Aliases.PANEL_ATTACHMENTS)
  private panelAttachments: PanelAPI;

  @Inject(Aliases.PANEL_FORM_SEND_ATTACHMENT)
  private panelFormSendAttachment: PanelAPI;

  @Inject(Aliases.TEXT_QUIESTIONNAIRE_TITLE)
  private textQuestionnaireTitle: TextAPI;

  @Inject(Aliases.ACTION_FINISH)
  private actionFinish: ActionAPI;

  @Inject(Aliases.ACTION_START)
  private actionStart: ActionAPI;

  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;

  @Inject(Aliases.FETCH_QUESTIONNAIRE)
  private fetchQuestionnaire: FetchAPI;

  @OnMount(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  onMountHpmsFormtechnicalVisit(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams() as unknown as Form;
    this.context.set({ nrSeqVisit, nrSeqForm });
    void this.permissionQuestionnaire(nrSeqVisit, nrSeqForm);
  }

  @OnMount(Aliases.LAYOUT_UPLOAD)
  onMountPanelFormSendAttachment(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams() as unknown as Form;
    this.context.set({ nrSeqVisit, nrSeqForm });
    void this.controlVisibilityPanels(Number(nrSeqVisit), Number(nrSeqForm), this.panelFormSendAttachment);
  }

  @OnMount(Aliases.FETCH_TECHNICAL_VISIT_FORMS_MENU_MOBILE)
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit, nrSeqForm });
    api.forceFetch();
  }

  @OnMount(Aliases.PANEL_MOBILE)
  onMountPanelMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  @OnAfterFetch([Aliases.FETCH_TECHNICAL_VISIT_FORMS_MENU, Aliases.FETCH_TECHNICAL_VISIT_FORMS_MENU_MOBILE])
  onAfterFetchFormsMenu(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Form[];
      const { nrSeqGroup, dsGroup } = data[0];
      this.context.set({ dsGroup, nrSeqGroup });
      this.textQuestionnaireTitle.setValue(dsGroup);
      if (!shouldRenderMobile()) {
        this.deck.selectCard(String(nrSeqGroup));
      } else {
        this.formMenuMobile.setData({ menuMobile: { dsGroup, nrSeqGroup } });
      }
    }
  }

  @OnAfterFetch(Aliases.FETCH_QUESTIONNAIRE)
  onAfterFetchQuestionary(): void {
    const { dsGroup } = this.context.getBulkData() as unknown as Form;
    this.textQuestionnaireTitle.setValue(dsGroup);
  }

  @OnSelectItem(Aliases.DECK_MENU)
  onSelectItemDeckMenu(event: SelectItemEvent<Form>): void {
    const { nrSeqForm } = this.historyService.getCurrentRouteParams();
    const { dsGroup, nrSeqGroup } = event.item;
    this.formSelected(dsGroup, nrSeqGroup, nrSeqForm as number);
  }

  @OnSubmit(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  onSubmitFormTechnicalVisit(event: FormAPI): void {
    this.handlerSubmit(event);
  }

  @OnSubmit(Aliases.FORM_SEND_ATTACHMENTS)
  onSubmit(event: SubmitEvent): void {
    const body = event.record;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    void HttpService.post(ENDPOINTS.POST.TECHNICAL_VISIT_ADDITIONAL_INFORMARTION, { nrSeqVisit }, { body })
      .then(() => {
        this.formSendAttachments.setRecord({});
        this.panelAttachments.setVisible(true);
        this.fetchAttachments.forceFetch();
      });
  }

  @OnSelectItem(Aliases.DECK)
  onSelect(event: SelectItemEvent<Form>): void {
    const { dsAttachment, nrSequence } = event.item;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams() as unknown as Form;
    const api = event.target as unknown as DeckAPI;
    api.unselectAll();
    DownloadService.of({
      endpoint: ENDPOINTS.GET.TECHNICAL_VISIT_ADDITIONAL_INFORMARTION_DOWNLOAD,
      name: dsAttachment,
      parameters: { nrSeqVisit, nrSequence, dsAttachment },
    });
  }

  @OnClick(Aliases.ACTION_START)
  async onClickActionStart(): Promise<void> {
    const { nrSeqVisit, nrSeqForm } = this.context.getBulkData() as unknown as Form;
    await HttpService.post(ENDPOINTS.POST.START_TECHNICAL_VISIT, { nrSeqVisit, nrSeqForm })
      .then(() => {
        void this.permissionQuestionnaire(nrSeqVisit, nrSeqForm);
        catchRenderException(() => void this.controlVisibilityPanels(Number(nrSeqVisit), Number(nrSeqForm), this.panelFormSendAttachment));
        ToastService.open({ message: 'hi.request-successful', level: ToastLevel.SUCCESS });
      }).catch((response) => {
        this.hpmsFormTechnicalVisit.setEditing(false);
        ToastService.open(response);
      });
  }

  @OnClick(Aliases.ACTION_FINISH)
  async onClickActionFinish(): Promise<void> {
    const { nrSeqVisit, nrSeqForm } = this.context.getBulkData() as unknown as Form;
    await HttpService.post(ENDPOINTS.POST.FINISH_TECHNICAL_VISIT, { nrSeqVisit, nrSeqForm })
      .then(() => {
        void this.permissionQuestionnaire(nrSeqVisit, nrSeqForm);
        catchRenderException(() => void this.controlVisibilityPanels(Number(nrSeqVisit), Number(nrSeqForm), this.panelFormSendAttachment));
        ToastService.open({ message: 'hi.request-successful', level: ToastLevel.SUCCESS });
      }).catch((response) => ToastService.open(response));
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onchange(event: ChangeEvent<{ dsGroup: string, nrSeqGroup: number }>): void {
    const { nrSeqForm } = this.historyService.getCurrentRouteParams();
    this.formSelected(event.value.dsGroup, event.value.nrSeqGroup, nrSeqForm as number);

  }
  private controlPanel(validation: boolean): void {
    this.panelGeneralForms.setVisible(!validation);
    this.panelAdditionalInformation.setVisible(validation);
  }

  private handlerSubmit(event: FormAPI): void {
    const recordsDsjustification = [];
    const recordsAutoComplete: { nrSeqFormPr: number; nrSeqAnswer: number | null; }[] = [];
    let nrSeqFormPr, nrSeqAnswer;

    for (const [key, value] of Object.entries(event.record)) {

      if (!key.includes('label__')) {
        if (key.includes('answers__')) {
          nrSeqFormPr = Number(key.split('answers__')[1]);
          nrSeqAnswer = (value as SubObject).code || null;
          recordsAutoComplete.push({ 'nrSeqFormPr': nrSeqFormPr, 'nrSeqAnswer': nrSeqAnswer });
        }

        if (key.includes('justification__')) {
          nrSeqFormPr = Number(key.split('justification__')[1]);
          recordsDsjustification.push({ 'nrSeqFormPr': nrSeqFormPr, 'dsJustification': value });
        }
      }
    }

    const answersForm = recordsDsjustification.map((justification) => ({
      ...recordsAutoComplete.find((o) => o.nrSeqFormPr === justification.nrSeqFormPr),
      ...justification,
    })) as unknown as any;

    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    void HttpService.post(ENDPOINTS.POST.TECHNICAL_VISIT_QUESTIONNAIRE, { nrSeqVisit }, { body: answersForm }).then(() => {
      this.fetchQuestionnaire.forceFetch();
      this.hpmsFormTechnicalVisit.setEditing(false);
      this.hpmsFormTechnicalVisit.setLocked(false);
    });
  }

  private async permissionQuestionnaire(nrSeqVisit: number, nrSeqForm: number): Promise<void> {
    await HttpService.get<Status>(ENDPOINTS.GET.TECHNICAL_VISIT_QUESTIONNAIRE_STATUS, { nrSeqVisit, nrSeqForm })
      .then((resp) => {
        const { ieAllowStart, ieAllowFinish, ieAllowEdit } = resp.body as unknown as Status;
        this.context.set({ ieAllowStart, ieAllowFinish, ieAllowEdit });
        catchRenderException(() => this.actionStart.setEnabled(ieAllowStart !== 'N' ? true : false));
        catchRenderException(() => this.actionFinish.setEnabled(ieAllowFinish !== 'N' ? true : false));
        catchRenderException(() => this.hpmsFormTechnicalVisit.setEditing(ieAllowEdit !== 'N' ? true : false));
      });
  }

  private async controlVisibilityPanels(nrSeqVisit: number, nrSeqForm: number, api: PanelAPI): Promise<void> {
    await HttpService.get<Status>(ENDPOINTS.GET.TECHNICAL_VISIT_QUESTIONNAIRE_STATUS, { nrSeqVisit, nrSeqForm })
      .then((resp) => {
        const { ieAllowStart, ieAllowFinish, ieAllowEdit } = resp.body as unknown as Status;
        this.context.set({ ieAllowStart, ieAllowFinish, ieAllowEdit });
        catchRenderException(() => api.setVisible(ieAllowEdit !== 'N' ? true : false));
      });
  }

  formSelected(dsGroup: string, nrSeqGroup: number, nrSeqForm: number): void {
    const { ieAllowEdit } = this.context.getBulkData() as unknown as Status;
    catchRenderException(() => {
      if (nrSeqGroup) {
        this.controlPanel(false);
        this.context.set({ dsGroup, nrSeqGroup, nrSeqForm });
        this.fetchQuestionnaire.setParameters({ dsGroup, nrSeqGroup, nrSeqForm });
        this.fetchQuestionnaire.forceFetch();
        this.textQuestionnaireTitle.setValue(dsGroup);
        this.hpmsFormTechnicalVisit.setEditing(ieAllowEdit !== 'N' ? true : false);
      } else {
        this.controlPanel(true);
      }
    });
  }
}
